import '@ant-design/v5-patch-for-react-19';
import { LogoutOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Button, Space } from 'antd';
import React from 'react';
import Footer from '@/components/Footer';
import { HeaderActions } from '@/components/RightContent';
import {
  collectAllowedRoutePaths,
  filterMenuData,
  isPathAccessible,
  resolveMenuSelectedKeys,
} from '@/utils/route';
import {
  isLoginPath,
  isAccountPath,
  loadInitialAppState,
  logoutCurrentSession,
  redirectToHome,
  redirectToForbidden,
  redirectToLogin,
  isForbiddenPath,
  isPasswordChangeRequired,
  ACCOUNT_SECURITY_PATH,
  type AppInitialState,
} from '@/utils/guard';
import { errorConfig } from './requestErrorConfig';

export const request = errorConfig;

const LOGO_SIZE = 44;

export async function getInitialState(): Promise<AppInitialState> {
  return loadInitialAppState();
}

export const layout = ({
  initialState,
  setInitialState,
}: {
  initialState?: AppInitialState;
  setInitialState?: (updater: (state: AppInitialState) => AppInitialState) => Promise<void> | void;
}) => ({
  logo: <img src="/logo.svg" alt="logo" style={{ width: 'auto', height: LOGO_SIZE, display: 'block' }} />,
  logout: async () => {
    await logoutCurrentSession(setInitialState, window.location.pathname + window.location.search);
  },
  onPageChange: () => {
    const pathname = window.location.pathname;

    if (isLoginPath(pathname)) {
      if (initialState?.currentUser) {
        redirectToHome(initialState);
      }
      return;
    }

    if (!initialState?.currentUser) {
      redirectToLogin(pathname);
      return;
    }

    if (isPasswordChangeRequired(initialState.currentUser)) {
      if (pathname !== ACCOUNT_SECURITY_PATH) {
        history.replace(ACCOUNT_SECURITY_PATH);
      }
      return;
    }

    if (isForbiddenPath(pathname)) {
      return;
    }

    if (isAccountPath(pathname)) {
      return;
    }

    const currentMenus = initialState.currentMenus || [];
    if (currentMenus.length > 0 && !isPathAccessible(pathname, currentMenus)) {
      redirectToForbidden();
    }
  },
  rightContentRender: () => <HeaderActions />,
  footerRender: () => <Footer />,
  menuFooterRender: () => {
    const currentUser = initialState?.currentUser;
    if (!currentUser?.username) {
      return null;
    }
    const displayName = currentUser.userNickname || currentUser.username;
    return (
      <div
        style={{
          padding: '12px 16px 20px',
          borderTop: '1px solid #eef2f7',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, #ffffff 100%)',
        }}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <div>
            <div
              style={{
                color: '#94a3b8',
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              当前账号
            </div>
            <div
              style={{
                color: '#0f172a',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: '22px',
                wordBreak: 'break-all',
              }}
            >
              {displayName}
            </div>
          </div>
          <Button
            block
            type="default"
            onClick={() => {
              history.push('/account/center');
            }}
          >
            个人中心
          </Button>
          <Button
            block
            icon={<LogoutOutlined />}
            onClick={async () => {
              await logoutCurrentSession(setInitialState, window.location.pathname + window.location.search);
            }}
          >
            退出登录
          </Button>
        </Space>
      </div>
    );
  },
  menuHeaderRender: undefined,
  headerTitleRender: (logo: React.ReactNode, title: React.ReactNode) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minWidth: 0,
      }}
    >
      {logo}
      <span
        style={{
          color: '#0f172a',
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
    </div>
  ),
  menuDataRender: (menuData: any[] = []) => {
    if (!initialState?.currentUser) {
      return menuData;
    }
    const allowedPaths = collectAllowedRoutePaths(
      initialState.currentUser,
      initialState.currentMenus || [],
    );
    return filterMenuData(menuData, allowedPaths);
  },
  menu: {
    locale: false,
  },
  menuProps: {
    selectedKeys: resolveMenuSelectedKeys(
      initialState?.currentMenus || [],
      window.location.pathname,
    ),
  },
});
